/**
 * Feb-2026 (User-Wunsch) — „Alle Dropdowns sollen sich die häufigst benutzten
 * Auswahlen merken und immer oben anzeigen."
 *
 * Drop-in-Ersatz für das shadcn <Select>-Muster im Inserat-Formular. Zeigt die
 * Top-N häufigst gewählten Optionen unter „★ Häufig verwendet" oben an und zählt
 * jede Auswahl mit (localStorage, pro `storageKey`).
 *
 * Verwendung:
 *   <FrequentSelect
 *      storageKey="listing.condition"
 *      value={form.condition}
 *      onValueChange={(v) => update("condition", v)}
 *      options={CONDITION_OPTIONS}            // string[] oder {value,label}[]
 *      placeholder="– bitte wählen –"
 *      triggerClassName={reqCls()}
 *      testId="listing-condition"
 *      noFreqValues={["any"]}                  // Sentinel-Werte nicht merken
 *   />
 */
import { Star } from "lucide-react";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectGroup,
} from "../ui/select";
import { recordSelection, getFrequency } from "../../lib/frequentOptions";

function normalize(options) {
    return (options || []).map((o) =>
        typeof o === "object" && o !== null ? { value: String(o.value), label: o.label ?? String(o.value) } : { value: String(o), label: String(o) }
    );
}

export default function FrequentSelect({
    storageKey,
    value,
    onValueChange,
    options,
    placeholder,
    triggerClassName = "",
    testId,
    topN = 3,
    noFreqValues = [],
    disabled = false,
}) {
    const opts = normalize(options);
    const freq = getFrequency(storageKey);
    const skip = new Set((noFreqValues || []).map(String));

    const frequentValues = opts
        .filter((o) => !skip.has(o.value) && (freq[o.value] || 0) > 0)
        .sort((a, b) => (freq[b.value] || 0) - (freq[a.value] || 0))
        .slice(0, topN)
        .map((o) => o.value);
    const frequentSet = new Set(frequentValues);

    const frequentOpts = frequentValues
        .map((v) => opts.find((o) => o.value === v))
        .filter(Boolean);
    const restOpts = opts.filter((o) => !frequentSet.has(o.value));

    const handleChange = (v) => {
        if (!skip.has(String(v))) recordSelection(storageKey, v);
        onValueChange?.(v);
    };

    return (
        <Select value={value} onValueChange={handleChange} disabled={disabled}>
            <SelectTrigger className={triggerClassName} data-testid={testId}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {frequentOpts.length > 0 && (
                    <>
                        <SelectGroup>
                            <SelectLabel className="flex items-center gap-1.5 text-[#111827]">
                                <Star className="w-3 h-3 fill-[#FFCC00] text-[#FFCC00]" /> Häufig verwendet
                            </SelectLabel>
                            {frequentOpts.map((o) => (
                                <SelectItem key={`freq-${o.value}`} value={o.value} data-testid={testId ? `${testId}-freq-${o.value}` : undefined}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectGroup>
                        <SelectSeparator />
                    </>
                )}
                {restOpts.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                        {o.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
